import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const DEFAULT_BREAK_SELECTOR = [
  "[data-pdf-section]",
  "table tr",
  "table thead",
  "table tbody",
  "h1",
  "h2",
  "h3",
].join(", ");

const getBreakpoints = (element, scaleY, selector) => {
  const elementRect = element.getBoundingClientRect();
  const nodes = Array.from(element.querySelectorAll(selector));

  return nodes
    .map((node) => {
      const rect = node.getBoundingClientRect();
      const top = (rect.top - elementRect.top + element.scrollTop) * scaleY;
      const height = rect.height * scaleY;

      return {
        top: Math.max(0, Math.round(top)),
        bottom: Math.max(0, Math.round(top + height)),
      };
    })
    .filter((point) => point.bottom > point.top)
    .sort((a, b) => a.top - b.top);
};

const getNextPageBreak = (startPx, maxEndPx, breakpoints, minSliceHeight) => {
  const candidates = breakpoints.filter(
    (point) => point.top > startPx + minSliceHeight && point.top <= maxEndPx
  );

  if (candidates.length > 0) {
    return candidates[candidates.length - 1].top;
  }

  const oversizedBlock = breakpoints.find(
    (point) => point.top <= maxEndPx && point.bottom > maxEndPx
  );

  if (oversizedBlock && oversizedBlock.top > startPx + minSliceHeight) {
    return oversizedBlock.top;
  }

  return maxEndPx;
};

export const exportElementToPaginatedPdf = async ({
  element,
  fileName,
  margin = 15,
  canvasOptions = {},
  breakpointSelector = DEFAULT_BREAK_SELECTOR,
}) => {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: "#ffffff",
    ...canvasOptions,
  });

  const pdf = new jsPDF("p", "mm", "a4");
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  const contentWidth = pdfWidth - 2 * margin;
  const pageHeight = pdfHeight - 2 * margin;

  const pageContentHeightPx = Math.floor((canvas.width * pageHeight) / contentWidth);
  const scaleY = canvas.height / element.scrollHeight;
  const minSliceHeight = Math.max(120, Math.floor(pageContentHeightPx * 0.35));
  const breakpoints = getBreakpoints(element, scaleY, breakpointSelector);

  let startPx = 0;
  let isFirstPage = true;

  while (startPx < canvas.height) {
    const maxEndPx = Math.min(startPx + pageContentHeightPx, canvas.height);
    const endPx =
      maxEndPx >= canvas.height
        ? canvas.height
        : getNextPageBreak(startPx, maxEndPx, breakpoints, minSliceHeight);

    const sliceHeightPx = Math.max(1, endPx - startPx);
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = sliceHeightPx;

    const pageContext = pageCanvas.getContext("2d");
    pageContext.fillStyle = "#ffffff";
    pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
    pageContext.drawImage(
      canvas,
      0,
      startPx,
      canvas.width,
      sliceHeightPx,
      0,
      0,
      canvas.width,
      sliceHeightPx
    );

    const pageImage = pageCanvas.toDataURL("image/png");
    const pageImageHeight = (sliceHeightPx * contentWidth) / canvas.width;

    if (!isFirstPage) {
      pdf.addPage();
    }

    pdf.addImage(pageImage, "PNG", margin, margin, contentWidth, pageImageHeight);

    startPx = endPx;
    isFirstPage = false;
  }

  pdf.save(fileName);
};
